import { useMemo } from "react";
import {
  SvgDelete,
  SvgEdit,
  SvgStatusError,
  SvgStatusSuccess,
} from "@itwin/itwinui-icons-react";
import {
  Button,
  Flex,
  IconButton,
  ProgressRadial,
  Surface,
  Text,
} from "@itwin/itwinui-react";
import { ISubscriptionDraft, ProvisionStatus } from "../../interfaces/IProvisioning";
import { IProducer } from "../../interfaces/IProducer";

export interface SubscriptionDraftCardProps {
  draft: ISubscriptionDraft;
  producers: IProducer[];
  /** Whether to show edit/delete buttons (only in draft mode) */
  editable?: boolean;
  onEdit?: (draftId: string) => void;
  onDelete?: (draftId: string) => void;
  onRetry?: (draftId: string) => void;
}

const StatusIcon = ({ status }: { status: ProvisionStatus }) => {
  switch (status) {
    case "loading":
      return <ProgressRadial size="small" indeterminate />;
    case "success":
      return <SvgStatusSuccess style={{ fill: "var(--iui-color-icon-positive)", width: 20, height: 20 }} />;
    case "error":
      return <SvgStatusError style={{ fill: "var(--iui-color-icon-negative)", width: 20, height: 20 }} />;
    default:
      return <div style={{ width: 20, height: 20 }} />;
  }
};

export const SubscriptionDraftCard = (props: SubscriptionDraftCardProps) => {
  const { draft, producers, editable = true, onEdit, onDelete, onRetry } = props;

  const producer = useMemo(
    () => producers.find((p) => p.id === draft.producerId),
    [producers, draft.producerId]
  );

  const producerName = producer?.name ?? "Unknown Producer";

  const eventSummary = useMemo(() => {
    if (draft.useAllEvents) {
      return "All events *";
    }
    const totalEvents = producer?.eventDefinitions?.length ?? 0;
    const selectedCount = draft.events.length;
    if (selectedCount === totalEvents && totalEvents > 0) {
      return `All ${totalEvents} events`;
    }
    return `${selectedCount} of ${totalEvents} events`;
  }, [draft.useAllEvents, draft.events.length, producer?.eventDefinitions?.length]);

  const filterSummary = useMemo(() => {
    const filters: string[] = [];
    if (draft.accountIdFilter) filters.push(`Account: ${draft.accountIdFilter}`);
    if (draft.iTwinIdFilter) filters.push(`iTwin: ${draft.iTwinIdFilter}`);
    if (draft.resourceDataCenterIdFilter) filters.push(`Resource DC: ${draft.resourceDataCenterIdFilter}`);
    if (draft.iTwinDataCenterIdFilter) filters.push(`iTwin DC: ${draft.iTwinDataCenterIdFilter}`);
    return filters.length > 0 ? filters.join(", ") : "No filters";
  }, [draft]);

  const isProvisioning = draft.provisionStatus === "loading";
  const hasError = draft.provisionStatus === "error";

  return (
    <Surface
      elevation={1}
      style={{
        padding: "12px",
        marginBottom: "8px",
        borderLeft: hasError ? "3px solid var(--iui-color-border-negative)" : undefined,
      }}
    >
      <Flex justifyContent="space-between" alignItems="flex-start">
        <Flex gap="s" alignItems="flex-start" style={{ flex: 1 }}>
          <StatusIcon status={draft.provisionStatus} />
          <div style={{ flex: 1 }}>
            <Text variant="leading" style={{ fontWeight: 600, marginBottom: "4px" }}>
              {producerName}
            </Text>
            <Text isMuted style={{ fontSize: "0.9em" }}>
              {eventSummary}
            </Text>
            <Text isMuted style={{ fontSize: "0.85em" }}>
              {filterSummary}
            </Text>
            {hasError && draft.errorMessage && (
              <Text style={{ color: "var(--iui-color-text-negative)", fontSize: "0.9em", marginTop: "4px" }}>
                Error: {draft.errorMessage}
              </Text>
            )}
          </div>
        </Flex>

        <Flex gap="xs" alignItems="center">
          {editable && !isProvisioning && draft.provisionStatus === "pending" && (
            <>
              <IconButton
                styleType="borderless"
                size="small"
                onClick={() => onEdit?.(draft.draftId)}
                title="Edit subscription"
              >
                <SvgEdit />
              </IconButton>
              <IconButton
                styleType="borderless"
                size="small"
                onClick={() => onDelete?.(draft.draftId)}
                title="Remove subscription"
              >
                <SvgDelete />
              </IconButton>
            </>
          )}
          {hasError && onRetry && (
            <Button size="small" onClick={() => onRetry(draft.draftId)}>
              Retry
            </Button>
          )}
        </Flex>
      </Flex>
    </Surface>
  );
};
